/**
 * Production Logging Configuration
 * Controls what gets logged in production vs development
 */

export const LOGGING_CONFIG = {
  // Enable/disable different types of logging
  enabled: {
    errors: true,           // Always log errors
    warnings: true,         // Always log warnings
    info: import.meta.env.DEV,     // Only in development
    debug: import.meta.env.DEV,    // Only in development
    performance: import.meta.env.DEV, // Only in development
    api: import.meta.env.DEV,       // Only in development
    cache: import.meta.env.DEV,     // Only in development
    database: import.meta.env.DEV,  // Only in development
    serviceWorker: import.meta.env.DEV, // Only in development
  },

  // Log levels
  levels: {
    ERROR: 0,
    WARN: 1,
    INFO: 2,
    DEBUG: 3
  },

  // Current log level based on environment
  currentLevel: import.meta.env.DEV ? 3 : 1, // DEBUG in dev, WARN in prod

  // Console methods to use for each level
  methods: {
    0: 'error',
    1: 'warn', 
    2: 'log',
    3: 'log'
  }
};

/**
 * Production-safe logging utility
 */
export class ProductionLogger {
  constructor() {
    this.config = LOGGING_CONFIG;
  }

  /**
   * Log based on level and configuration
   */
  log(level, category, message, ...args) {
    if (level > this.config.currentLevel) return;
    if (!this.config.enabled[category]) return;

    const method = this.config.methods[level];
    const prefix = `[${category.toUpperCase()}]`;
    
    if (console[method]) {
      console[method](prefix, message, ...args);
    }
  }

  error(category, message, ...args) {
    this.log(0, category, message, ...args);
  }

  warn(category, message, ...args) {
    this.log(1, category, message, ...args);
  }

  info(category, message, ...args) {
    this.log(2, category, message, ...args);
  }

  debug(category, message, ...args) {
    this.log(3, category, message, ...args);
  }
}

// Create singleton instance
export const logger = new ProductionLogger();

// Export convenience methods
export const logError = (category, message, ...args) => logger.error(category, message, ...args);
export const logWarn = (category, message, ...args) => logger.warn(category, message, ...args);
export const logInfo = (category, message, ...args) => logger.info(category, message, ...args);
export const logDebug = (category, message, ...args) => logger.debug(category, message, ...args);

export default logger;
